/* Centralized HTTP client built on fetch with baseURL, JSON handling, and auth token support. */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpOptions<TBody = unknown> {
  path: string;
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  // When true, includes credentials (cookies) in request. Useful when backend uses cookies for auth.
  withCredentials?: boolean;
  // Override baseURL (default comes from env)
  baseURL?: string;
}

export interface HttpResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

const DEFAULT_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

function getBaseURL() {
  // Vite exposes env vars on import.meta.env
  // Ensure trailing slash so relative paths resolve correctly with new URL()
  const ensureTrailingSlash = (u: string) => (u.endsWith('/') ? u : `${u}/`);
  const raw = (
    (import.meta as any).env?.VITE_API_BASE_URL ||
    (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api')
  );
  // new URL() requires an absolute base URL (scheme + host).
  // When VITE_API_BASE_URL is a relative path like "/api/", prepend the current origin.
  const base =
    raw.startsWith('http://') || raw.startsWith('https://')
      ? raw
      : typeof window !== 'undefined'
        ? `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`
        : raw;
  return ensureTrailingSlash(base);
}

function getAccessToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

export async function http<TResp = unknown, TBody = unknown>(options: HttpOptions<TBody>): Promise<HttpResponse<TResp>> {
  const { path, method = 'GET', body, headers = {}, withCredentials = false, baseURL } = options;

  const urlBase = baseURL ?? getBaseURL();
  const url = new URL(path, urlBase);

  const token = getAccessToken();
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  const init: RequestInit = {
    method,
    headers: {
      ...DEFAULT_HEADERS,
      ...authHeader,
      ...headers,
    } as Record<string, string>,
    credentials: withCredentials ? 'include' : 'same-origin',
  };

  if (body !== undefined && method !== 'GET') {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = typeof body === 'string' ? (body as string) : JSON.stringify(body);
  }

  // Dev-only diagnostics to help debug unauthorized issues.
  // Bewusst OHNE Header/Token, damit keine Zugangsdaten in der Konsole landen.
  if ((import.meta as any).env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[http]', method, url.toString(), {
      hasAuth: Boolean(token),
      withCredentials,
    });
  }

  const resp = await fetch(url.toString(), init);

  // Signalisiert dem SessionContext, dass eine Backend-Kommunikation stattgefunden hat
  // (jede Antwort, auch Fehler, zählt als Aktivität gegen den Inaktivitäts-Logout).
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('whik:api-activity'));
    }
  } catch {}

  const contentType = resp.headers.get('Content-Type') || '';
  const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');

  let data: any = null;
  try {
    data = isJson ? await resp.json() : await resp.text();
  } catch {
    data = null;
  }

  if (!resp.ok) {
    // Bei Authentifizierungs-/Autorisierungsfehlern eine zentrale Abmeldung auslösen.
    // 401 = Token fehlt/abgelaufen/ungültig. Der SessionContext fängt das Event ab,
    // leert den Auth-Storage und leitet zum Login weiter.
    // 403 (Forbidden) wird NICHT ausgeloggt: Der Nutzer ist gültig angemeldet, ihm
    // fehlt nur die Rolle (z. B. Nicht-Admin ruft Admin-Endpunkt) – hier soll die
    // aufrufende Seite eine Fehlermeldung zeigen, nicht die Session beendet werden.
    if (resp.status === 401) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('whik:auth-expired'));
        }
      } catch {}
    }

    const error: any = new Error('HTTP Error');
    error.status = resp.status;
    error.data = data;
    throw error;
  }

  return { data: data as TResp, status: resp.status, ok: resp.ok };
}

export const api = {
  get: <T = unknown>(path: string, headers?: Record<string, string>) =>
    http<T>({ path, method: 'GET', headers }),
  post: <T = unknown, B = unknown>(path: string, body?: B, headers?: Record<string, string>) =>
    http<T, B>({ path, method: 'POST', body, headers }),
  put: <T = unknown, B = unknown>(path: string, body?: B, headers?: Record<string, string>) =>
    http<T, B>({ path, method: 'PUT', body, headers }),
  patch: <T = unknown, B = unknown>(path: string, body?: B, headers?: Record<string, string>) =>
    http<T, B>({ path, method: 'PATCH', body, headers }),
  delete: <T = unknown>(path: string, headers?: Record<string, string>) =>
    http<T>({ path, method: 'DELETE', headers }),
};
